import React from 'react';
import { getContext, useCombinedRefs } from 'common';
import _merge from 'lodash/merge';
import Typography, { TypographyProps } from 'components/Typography';
import useMeasure from 'react-use-measure';
import ResizeObserver from 'resize-observer-polyfill';
import { SnackbarContext } from 'components/Snackbar';
import Styled from './styles';

export interface SnackbarContentProps extends React.ComponentPropsWithRef<'div'> {
  /** The content of the message. */
  children: React.ReactNode;
  /** To override the theme please refer to the "Readme" tab to learn more. */
  theme?: any;
  /** The Typography configuration. See `<Typography/>` documentation for more. */
  typographyProps?: Partial<TypographyProps>;
}

/** SnackbarContent, place snackbar message here. All other props will be provided to the wrapper element. */
const SnackbarContent = React.forwardRef<HTMLElement, SnackbarContentProps>((props: SnackbarContentProps, ref) => getContext(({ RTL, theme: globalTheme }) => {
  const {
    children,
    theme,
    typographyProps,
    ...otherProps
  } = props;
  const resolvedTheme = _merge({}, globalTheme.SNACKBAR, theme);
  const [heightRef, { height }] = useMeasure({ polyfill: ResizeObserver });
  const combinedRef = useCombinedRefs(heightRef, typographyProps.ref);
  const { setContentHeight } = React.useContext(SnackbarContext);

  // update parent styling based on content height (dynamic padding required based on content height)
  React.useLayoutEffect(() => {
    setContentHeight(height);
  }, [height]);

  return (
    <Styled.SnackbarContent
      ref={ref}
      $rtl={RTL}
      $theme={resolvedTheme}
      {...otherProps}
    >
      <Typography ref={combinedRef} variant="BODY" theme={resolvedTheme} {...typographyProps}>
        {children}
      </Typography>
    </Styled.SnackbarContent>
  );
}));

SnackbarContent.displayName = 'SnackbarContent';

SnackbarContent.defaultProps = {
  typographyProps: {},
  theme: {},
};

export default SnackbarContent;
